/**
 * WebSocket subscription transport with automatic reconnection
 *
 * This module provides WebSocket transport implementation
 * for reliable GraphQL subscriptions with automatic
 * reconnection and keep-alive support using Apollo's official GraphQLWsLink.
 *
 * Authentication is handled via connection parameters in the WebSocket payload.
 */
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { GRAPHQL_API_URL } from '@config/api';

/**
 * Convert HTTP URL to WebSocket URL
 */
const getWebSocketUrl = (httpUrl: string): string => {
  const url = new URL(httpUrl);

  // Convert protocol
  if (url.protocol === 'https:') {
    url.protocol = 'wss:';
  } else if (url.protocol === 'http:') {
    url.protocol = 'ws:';
  }

  return url.toString();
};

/**
 * Enhanced WebSocket client factory with better error categorization
 */
const createEnhancedWebSocketClient = (getToken?: () => Promise<string | null>) => {
  // Enhanced error categorization
  const categorizeWebSocketError = (error: unknown): 'auth' | 'network' | 'server' | 'connection_limit' | 'rate_limit' | 'protocol' | 'unknown' => {
    const errorMessage = String(error).toLowerCase();
    const errorRecord = error as Record<string, unknown>;
    const errorCode = errorRecord?.code || (errorRecord?.extensions as Record<string, unknown>)?.code;

    // Authentication errors
    if (errorMessage.includes('unauthorized') ||
        errorMessage.includes('authentication') ||
        errorMessage.includes('forbidden') ||
        errorCode === 'AUTHENTICATION_REQUIRED' ||
        errorCode === 'UNAUTHENTICATED' ||
        errorCode === 'UNAUTHORIZED') {
      return 'auth';
    }

    // Connection limit errors
    if (errorMessage.includes('connection limit') ||
        errorMessage.includes('too many connections') ||
        errorCode === 'CONNECTION_LIMIT_EXCEEDED') {
      return 'connection_limit';
    }

    // Rate limit errors
    if (errorMessage.includes('rate limit') ||
        errorMessage.includes('too many requests') ||
        errorCode === 'RATE_LIMITED' ||
        errorCode === 'TOO_MANY_REQUESTS') {
      return 'rate_limit';
    }

    // Protocol errors
    if (errorMessage.includes('protocol') ||
        errorMessage.includes('invalid frame') ||
        errorMessage.includes('websocket') ||
        errorCode === 'PROTOCOL_ERROR') {
      return 'protocol';
    }

    // Network errors
    if (errorMessage.includes('network') ||
        errorMessage.includes('fetch') ||
        errorMessage.includes('connection') ||
        errorMessage.includes('timeout') ||
        errorCode === 'NETWORK_ERROR') {
      return 'network';
    }

    // Server errors
    if (errorMessage.includes('500') ||
        errorMessage.includes('internal server') ||
        errorMessage.includes('server error') ||
        errorCode === 'INTERNAL_SERVER_ERROR') {
      return 'server';
    }

    return 'unknown';
  };

  // Get auth token for connection params with comprehensive logging
  const getConnectionParams = async (): Promise<Record<string, unknown>> => {
    let connectionParams: Record<string, unknown> = {};

    if (getToken) {
      console.log('[WebSocket Auth] Attempting to get authentication token...');
      const tokenStartTime = performance.now();

      try {
        const token = await getToken();
        const tokenEndTime = performance.now();
        const tokenDuration = Math.round(tokenEndTime - tokenStartTime);

        if (token) {
          // Log successful token retrieval (without exposing the actual token)
          console.log('[WebSocket Auth] ✅ Token retrieved successfully', {
            tokenLength: token.length,
            retrievalTime: `${tokenDuration}ms`,
            tokenPrefix: `${token.substring(0, 10)}...`,
            hasBearer: token.startsWith('Bearer '),
            timestamp: new Date().toISOString()
          });

          connectionParams = {
            Authorization: `Bearer ${token}`,
          };

          console.log('[WebSocket Auth] Connection params prepared', {
            hasAuthorization: !!connectionParams.Authorization,
            paramCount: Object.keys(connectionParams).length,
            authHeaderFormat: typeof connectionParams.Authorization === 'string' ? 'string' : typeof connectionParams.Authorization
          });
        } else {
          console.warn('[WebSocket Auth] ⚠️ Token retrieval returned null/undefined', {
            tokenValue: token,
            retrievalTime: `${tokenDuration}ms`,
            timestamp: new Date().toISOString()
          });
        }
      } catch (error) {
        const tokenEndTime = performance.now();
        const tokenDuration = Math.round(tokenEndTime - tokenStartTime);

        console.error('[WebSocket Auth] ❌ Failed to get auth token', {
          error: error instanceof Error ? error.message : String(error),
          errorType: error instanceof Error ? error.constructor.name : typeof error,
          retrievalTime: `${tokenDuration}ms`,
          timestamp: new Date().toISOString()
        });
      }
    } else {
      console.warn('[WebSocket Auth] ⚠️ No getToken function provided - WebSocket will connect without authentication');
    }

    return connectionParams;
  };

  // Log WebSocket connection attempt details
  const wsUrl = getWebSocketUrl(GRAPHQL_API_URL);
  console.log('[WebSocket] Initiating connection', {
    url: wsUrl,
    timestamp: new Date().toISOString()
  });

  // Create WebSocket client with enhanced error handling
  return createClient({
    url: wsUrl,
    connectionParams: getConnectionParams,
    keepAlive: 10_000, // 10 seconds
    retryAttempts: Infinity,
    shouldRetry: (error) => {
      const errorCategory = categorizeWebSocketError(error);

      // Don't retry authentication, connection limit, or rate limit errors
      if (errorCategory === 'auth' || errorCategory === 'connection_limit' || errorCategory === 'rate_limit') {
        console.log(`[WebSocket] Not retrying ${errorCategory} error:`, error);
        return false;
      }

      console.log(`[WebSocket] Will retry ${errorCategory} error:`, error);
      return true;
    },
    retryWait: async (retries) => {
      // Exponential backoff with jitter
      const baseDelay = 1000; // 1 second
      const maxDelay = 30000; // 30 seconds
      const delay = Math.min(baseDelay * Math.pow(2, retries), maxDelay);
      const jitter = Math.random() * 1000;

      console.log(`[WebSocket] Reconnecting in ${Math.round(delay + jitter)}ms (attempt ${retries + 1})...`);
      await new Promise(resolve => setTimeout(resolve, delay + jitter));
    },
    on: {
      connected: () => {
        console.log('[WebSocket] ✅ Connected successfully', {
          timestamp: new Date().toISOString(),
          authMethod: 'connection_params'
        });
      },
      connecting: () => {
        console.log('[WebSocket] 🔄 Connecting with connection params auth...', {
          timestamp: new Date().toISOString()
        });
      },
      closed: (event) => {
        const eventRecord = event as Record<string, unknown>;
        const errorCategory = eventRecord?.code ? categorizeWebSocketError({ code: eventRecord.code }) : 'unknown';
        console.log('[WebSocket] 🔌 Connection closed', {
          event,
          timestamp: new Date().toISOString(),
          code: eventRecord?.code,
          reason: eventRecord?.reason,
          wasClean: eventRecord?.wasClean,
          errorCategory
        });
      },
      error: (error) => {
        const errorCategory = categorizeWebSocketError(error);
        console.error(`[WebSocket] ❌ Connection error (${errorCategory})`, {
          error: error instanceof Error ? error.message : String(error),
          errorType: error instanceof Error ? error.constructor.name : typeof error,
          timestamp: new Date().toISOString(),
          category: errorCategory
        });
      }
    },
  });
};

/**
 * Create GraphQL WebSocket link using Apollo's official implementation
 */
export const createWebSocketLink = (getToken?: () => Promise<string | null>) => {
  console.log('[WebSocket] Creating GraphQLWsLink with enhanced error handling');

  return new GraphQLWsLink(createEnhancedWebSocketClient(getToken));
};

/**
 * Create subscription link using WebSocket transport
 * Provides automatic reconnection and keep-alive support
 */
export const createSubscriptionLink = (getToken?: () => Promise<string | null>) => {
  console.log('[WebSocket] Creating subscription link');
  return createWebSocketLink(getToken);
};
